/**
 * useExpectedAspectSetup.ts
 *
 * Derives the setup view's rows from the server's `effective.expectedAspects`:
 * - re-evaluates composed aspects against the live form values, never touching a property
 *   the server did not clear for the viewer (readDenied = P − evaluatedProperties);
 * - groups rows by section, then the synthetic "not on type" and "attached" groups;
 * - builds the reason text from each source's Archetype display name;
 * - logs `expected_aspect_completed` when a server read that follows a save shows a flagged
 *   aspect as filled (live typing alone never logs).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'localization';
import { useAnalytics } from '@center-technologies/analytics';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { evaluateAspectPresence, isExpectedAspectNeedingAttention } from '@center-platform/spheres/validation';
import type { SlotName } from 'types/attachment';
import type {
  EffectiveExpectedAspect,
  ExpectedAspectExpectation,
  ExpectedAspectPlacement,
  ExpectedAspectSource,
  ExpectedAspectValueState,
  ExpectedAspectsEvaluation,
} from 'types/archetype';
import type { LocalizedString } from 'types/context';
import type { Schema, SchemaSection } from 'types/schema';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import { compareSectionGroups, DEFAULT_SECTION_KEY, resolveAspectSection } from '../utils/resolveSectionLayout';

export const NOT_ON_TYPE_GROUP_KEY = '__not_on_type';
export const ATTACHED_GROUP_KEY = '__attached';

export interface ExpectedAspectRow {
  aspectSystemName: string;
  expectation: ExpectedAspectExpectation;
  placement: ExpectedAspectPlacement;
  present: boolean;
  /** Live for composed rows the server evaluated; the server's value otherwise. */
  valueState: ExpectedAspectValueState | null;
  needsAttention: boolean;
  aspectName: LocalizedString | null;
  sources: ExpectedAspectSource[];
  evaluatedProperties: string[];
  hasWithheld: boolean;
  /**
   * Some evaluated property with no value can be filled in by a person (`!read_only && !hidden`).
   * A composed row with nothing fillable left reads "Filled in automatically" and never flags.
   */
  fillable: boolean;
  /** Some evaluated property renders on the page, so Show has a field to take the person to. */
  hasShownProperty: boolean;
  /** Localized "Required by Project; recommended by Team". */
  reasonText: string;
  /** Section system_name, `__default`, `__not_on_type` or `__attached`. */
  groupKey: string;
}

export interface ExpectedAspectGroup {
  groupKey: string;
  rows: ExpectedAspectRow[];
  needsAttentionCount: number;
}

export interface UseExpectedAspectSetupResult {
  rows: ExpectedAspectRow[];
  groups: ExpectedAspectGroup[];
  needsAttentionCount: number;
  needsAttentionBySection: Record<string, number>;
  evaluation?: ExpectedAspectsEvaluation;
  /** Records that the person was taken to this aspect, for `viaSetupView` on completion. */
  markRevealed: (aspectSystemName: string) => void;
}

export interface UseExpectedAspectSetupOptions {
  sphereId: string | undefined;
  expectedAspects: EffectiveExpectedAspect[];
  evaluation?: ExpectedAspectsEvaluation;
  /** The sphere's assigned Archetypes, for display names in the reason text. */
  archetypes: Array<{ systemName: string; displayName: string }>;
  /** The merged schema; `properties`, `sections` and `aspects` are read. */
  schema: Pick<Schema, 'properties' | 'sections' | 'aspects'> | undefined;
  /** Live form values (`values.properties`). */
  values: Record<string, unknown>;
  /** Changes after each save; a server read that follows one may complete an aspect. */
  refreshKey?: unknown;
}

const NO_SLOTS: ReadonlySet<SlotName> = new Set<SlotName>();
const EMPTY_PROPERTIES: NonNullable<UseExpectedAspectSetupOptions['schema']>['properties'] = [];

function lowerFirst(text: string, locale: string): string {
  return text.charAt(0).toLocaleLowerCase(locale) + text.slice(1);
}

export function useExpectedAspectSetup(options: UseExpectedAspectSetupOptions): UseExpectedAspectSetupResult {
  const { sphereId, expectedAspects, evaluation, archetypes, schema, values, refreshKey } = options;
  const { t, i18n } = useTranslation();
  const logAnalyticsEvent = useAnalytics();
  const graphClient = useApiClientsOrNull()?.graphClient;

  const revealedRef = useRef(new Set<string>());
  const [filledSlots, setFilledSlots] = useState<ReadonlySet<SlotName>>(NO_SLOTS);
  // Keep the same Set when nothing changed so an unchanged cache read does not re-derive every row.
  const replaceFilledSlots = useCallback((next: ReadonlySet<SlotName>) => {
    setFilledSlots((prev) => (prev.size === next.size && [...next].every((n) => prev.has(n)) ? prev : next));
  }, []);

  // Slots with a row on the sphere come from the shared sphere cache (standards/frontend/sphere-avatar.md).
  // A sphere that expects nothing has no row to evaluate, so it makes no read.
  const hasExpectations = expectedAspects.length > 0;
  useEffect(() => {
    if (!sphereId || !graphClient || !hasExpectations) {
      replaceFilledSlots(NO_SLOTS);
      return undefined;
    }
    let cancelled = false;
    globalSphereCache
      .getSphereData(sphereId, graphClient)
      .then((data) => {
        if (cancelled) return;
        const slots = new Set<SlotName>();
        // A legacy-imageId picture renders as the avatar, so it counts exactly as a slot row does.
        if (data.slotAvatar) slots.add('avatar');
        replaceFilledSlots(slots);
      })
      .catch(() => {
        if (!cancelled) replaceFilledSlots(NO_SLOTS);
      });
    return () => {
      cancelled = true;
    };
  }, [sphereId, graphClient, hasExpectations, refreshKey, replaceFilledSlots]);

  const properties = schema?.properties ?? EMPTY_PROPERTIES;
  const sections = schema?.sections;

  const reasonFor = useCallback(
    (sources: ExpectedAspectSource[]): string => {
      const language = i18n.language;
      const displayNames = new Map(archetypes.map((a) => [a.systemName, a.displayName]));
      const list = new Intl.ListFormat(language, { type: 'conjunction' });
      const fallback = t('archetypes.expected.reason.unknownArchetype');
      const namesOf = (group: ExpectedAspectSource[]) =>
        list.format(group.map((s) => displayNames.get(s.archetypeSystemName) ?? fallback));

      const expectations = [...new Set(sources.map((s) => s.expectation))];
      if (sources.length === 1) return t('archetypes.expected.reason.single', { names: namesOf(sources) });
      return expectations
        .map((expectation, index) => {
          const text = t(`archetypes.expected.reason.${expectation}`, {
            names: namesOf(sources.filter((s) => s.expectation === expectation)),
          });
          return index === 0 ? text : lowerFirst(text, language);
        })
        .join('; ');
    },
    [archetypes, i18n.language, t]
  );

  const rows = useMemo(
    () =>
      expectedAspects.map((aspect): ExpectedAspectRow => {
        const aspectProperties = properties.filter((p) => p.aspect_system_name === aspect.aspectSystemName);
        const evaluatesLive =
          evaluation !== 'failed' &&
          aspect.placement === 'composed' &&
          aspect.valueState !== null &&
          aspect.valueState !== 'not_visible' &&
          aspectProperties.length > 0;

        let valueState = aspect.valueState;
        let evaluatedProperties = aspect.evaluatedProperties;
        let hasWithheld = aspect.hasWithheld;
        let needsAttention = evaluation === 'failed' ? false : aspect.needsAttention;
        // Without the aspect's properties on the client, only an aspect the server evaluated no
        // property of is known to have nothing to fill in or show.
        let fillable = aspect.evaluatedProperties.length > 0;
        let hasShownProperty =
          aspectProperties.length > 0 ? aspectProperties.some((p) => !p.hidden) : aspect.evaluatedProperties.length > 0;

        if (evaluatesLive) {
          const cleared = new Set(aspect.evaluatedProperties);
          const readDenied = new Set(aspectProperties.filter((p) => !cleared.has(p.name)).map((p) => p.name));
          const result = evaluateAspectPresence({
            aspectSystemName: aspect.aspectSystemName,
            properties: aspectProperties,
            values,
            readDenied,
            filledSlots,
          });
          valueState = result.valueState;
          evaluatedProperties = result.evaluatedProperties;
          hasWithheld = result.hasWithheld;
          fillable = result.fillable;
          const evaluatedSet = new Set(result.evaluatedProperties);
          hasShownProperty = aspectProperties.some((p) => evaluatedSet.has(p.name) && !p.hidden);
          needsAttention = isExpectedAspectNeedingAttention(aspect.expectation, aspect.placement, result);
        }

        let groupKey = NOT_ON_TYPE_GROUP_KEY;
        if (aspect.placement === 'attached') groupKey = ATTACHED_GROUP_KEY;
        else if (aspect.placement === 'composed') {
          const section = schema ? resolveAspectSection(schema as Schema, aspect.aspectSystemName) : null;
          groupKey = section && sections?.some((s) => s.system_name === section) ? section : DEFAULT_SECTION_KEY;
        }

        return {
          aspectSystemName: aspect.aspectSystemName,
          expectation: aspect.expectation,
          placement: aspect.placement,
          present: aspect.present,
          valueState,
          needsAttention,
          aspectName: aspect.aspectName,
          sources: aspect.sources,
          evaluatedProperties,
          hasWithheld,
          fillable,
          hasShownProperty,
          reasonText: reasonFor(aspect.sources),
          groupKey,
        };
      }),
    [expectedAspects, evaluation, properties, schema, sections, values, filledSlots, reasonFor]
  );

  const groups = useMemo(() => {
    const byKey = new Map<string, ExpectedAspectRow[]>();
    for (const row of rows) byKey.set(row.groupKey, [...(byKey.get(row.groupKey) ?? []), row]);

    const sectionOf = (key: string): SchemaSection | null => sections?.find((s) => s.system_name === key) ?? null;
    const sectionKeys = [...byKey.keys()]
      .filter((key) => key !== NOT_ON_TYPE_GROUP_KEY && key !== ATTACHED_GROUP_KEY)
      .map((key) => ({ key, section: key === DEFAULT_SECTION_KEY ? null : sectionOf(key) }))
      .sort(compareSectionGroups)
      .map((g) => g.key);

    return [...sectionKeys, NOT_ON_TYPE_GROUP_KEY, ATTACHED_GROUP_KEY]
      .filter((key) => byKey.has(key))
      .map((groupKey): ExpectedAspectGroup => {
        const groupRows = byKey.get(groupKey) ?? [];
        return { groupKey, rows: groupRows, needsAttentionCount: groupRows.filter((r) => r.needsAttention).length };
      });
  }, [rows, sections]);

  const needsAttentionCount = useMemo(() => rows.filter((r) => r.needsAttention).length, [rows]);

  const needsAttentionBySection = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const row of rows) {
      if (row.needsAttention) counts[row.groupKey] = (counts[row.groupKey] ?? 0) + 1;
    }
    return counts;
  }, [rows]);

  // Completion is judged on server reads only: the previous read flagged the aspect and the read
  // that follows a save (a refreshKey change) shows it filled. Live values never reach this.
  const lastServerReadRef = useRef(expectedAspects);
  const lastRefreshKeyRef = useRef(refreshKey);
  const awaitingReadRef = useRef(false);
  useEffect(() => {
    if (refreshKey !== lastRefreshKeyRef.current) {
      lastRefreshKeyRef.current = refreshKey;
      awaitingReadRef.current = true;
    }
    if (expectedAspects === lastServerReadRef.current) return;

    const previous = lastServerReadRef.current;
    lastServerReadRef.current = expectedAspects;
    if (!awaitingReadRef.current || !sphereId) return;
    awaitingReadRef.current = false;

    for (const aspect of expectedAspects) {
      const before = previous.find((p) => p.aspectSystemName === aspect.aspectSystemName);
      if (before?.needsAttention && aspect.valueState === 'filled') {
        logAnalyticsEvent('expected_aspect_completed', undefined, {
          sphereId,
          aspectSystemName: aspect.aspectSystemName,
          expectation: 'required',
          viaSetupView: revealedRef.current.has(aspect.aspectSystemName),
        });
      }
    }
  }, [expectedAspects, refreshKey, sphereId, logAnalyticsEvent]);

  const markRevealed = useCallback((aspectSystemName: string) => {
    revealedRef.current.add(aspectSystemName);
  }, []);

  return { rows, groups, needsAttentionCount, needsAttentionBySection, evaluation, markRevealed };
}
